import type { ServiceObjectsServiceObjectRevisionCategory } from 'api/models/mainservice';
import type {
  CheckListInputValue,
  CheckListItemValue,
  CompareDataWithTemplateResult,
  CustomDataField,
  CustomDataFieldValue,
  CustomDataObject,
  CustomDataSection,
  CustomDataSectionValues,
  DefaultValue,
  LinkedDataObject,
  LinkedDataServiceObjectInCustomer,
  MappedErrorsSection,
  MappedFieldError,
  MappedSectionWithData,
  Option,
  RelatedFieldObject,
  RelatedFieldTransformType,
  ServiceObjectCategory,
  ServiceObjectFieldValue,
  SubmittedCustomData,
  TotalCostHidePriceParams
} from 'common/models';
import dayjs from 'dayjs';
import lodashGet from 'lodash.get';
import type { RelatedDataInterventionAssignation } from 'pages/interventions/models';
import type { OrderMaterial, TotalCostData, TravelCost, Work } from 'pages/orders/models';
import type {
  ServiceObjectInCustomer,
  ServiceObjectInRelatedData
} from 'pages/serviceObjects/models';
import type { FieldError } from 'react-hook-form';
import { getI18n } from 'react-i18next';

import { oneLineAddress } from './addressHelpers';
import { getFullName } from './contactPersonHelpers';
import guidGenerator from './guidGenerator';
import { valueCurrencyShortcut } from './priceHelpers';
import { getNow } from './timeHelpers';

export const CHECKLIST_OPTION_ID_PREFIX = 'option-';

export const getMappedCustomData = (
  sections: CustomDataSection[],
  customData?: CustomDataObject
): MappedSectionWithData[] => {
  return sections.map((section) => {
    const sectionData = customData?.sections
      ? customData.sections.find((x) => x.id === section.id)?.data
      : [];
    return {
      id: section.id,
      title: section.title,
      fieldsWithData: section.fields
        ? section.fields.map((field) => {
            const fieldData = (sectionData as CustomDataFieldValue[])?.find(
              (x) => x.id === field.id
            );

            return {
              id: field.id,
              formField: field,
              label: field.label,
              value: fieldData?.value,
              stringValue: fieldData?.stringValue
            };
          })
        : []
    };
  });
};

export const getFieldInitialValues = (
  field: CustomDataFieldValue,
  templateField?: CustomDataField
) => {
  if (templateField?.type === 'Select' && !templateField.isMultiSelect) {
    return { value: field.value, stringValue: field.stringValue };
  }

  if (templateField?.type === 'CheckList') {
    return (field.value as CheckListItemValue[]).reduce(
      (acc, checkListItemValue: CheckListItemValue) => {
        // Prefix the option ID so the select always returns the complete option object.
        const optionId = `${CHECKLIST_OPTION_ID_PREFIX}${checkListItemValue.id}`;

        acc[optionId] = {
          description: checkListItemValue.description,
          value: getCheckListItemValueText(checkListItemValue.value)
        };

        return acc;
      },
      {} as any
    );
  }

  return field.value;
};

export const getSectionInitialValues = (
  values: CustomDataFieldValue[],
  templateSection?: CustomDataSection
) => {
  return values.reduce((acc: any, field) => {
    const templateField = templateSection?.fields?.find((x) => x.id === field.id);

    acc[field.id] = getFieldInitialValues(field, templateField);
    return acc;
  }, {});
};

export const getServiceObjectsInitialValues = (
  values: ServiceObjectFieldValue[],
  templateSection?: CustomDataSection
) => {
  return values.reduce((acc: { [serviceObjectId: string]: any }, serviceObject) => {
    acc[serviceObject.serviceObjectId] = {
      ...serviceObject.fields.reduce((acc: any, field) => {
        const templateField = templateSection?.fields?.find((x) => x.id === field.id);

        acc[field.id] = getFieldInitialValues(field, templateField);
        return acc;
      }, {})
    };
    return acc;
  }, {});
};

const getCheckListItemValue = (value?: string) => {
  if (value === 'ok') {
    return true;
  }

  if (value === 'notOk') {
    return false;
  }

  return undefined;
};

const getCheckListItemValueText = (value?: boolean) => {
  if (value === false) {
    return 'notOk';
  }

  if (value === true) {
    return 'ok';
  }

  return undefined;
};

export const getFieldsValuesForSubmit = (
  section: CustomDataSection,
  sectionValues: {
    [key: string]: any;
  }
) =>
  section.fields
    ? section.fields.map((field) => {
        // No value, return empty field
        if (!sectionValues[field.id] && sectionValues[field.id] !== 0) {
          if (field.type === 'Select' && field.isMultiSelect) {
            return {
              id: field.id,
              value: []
            };
          }

          return {
            id: field.id,
            value: null
          };
        }

        if (field.type === 'Select') {
          if (field.isMultiSelect) {
            // When multi choice, we return {id, value:[{value,stringValue}]}
            return {
              id: field.id,
              value: [...sectionValues[field.id]]
            };
          }

          // When single choice, we return {id, value, stringValue}
          return {
            ...sectionValues[field.id],
            id: field.id
          };
        }

        if (field.type === 'CheckList') {
          const fieldValues = sectionValues[field.id] as Record<string, CheckListInputValue>;

          let mappedValues: CheckListItemValue[] = Object.keys(fieldValues).map(
            (checklistItemKey) => {
              // Prefix the option ID so the select always returns the complete option object.
              // So we must split the key to get real option id
              const splittedKey = checklistItemKey.split(CHECKLIST_OPTION_ID_PREFIX);

              return {
                id: splittedKey[1],
                description: fieldValues[checklistItemKey].description,
                value: getCheckListItemValue(fieldValues[checklistItemKey].value)
              };
            }
          );

          return {
            id: field.id,
            value: mappedValues
          };
        }

        // Other fields, we return {id, value}
        return {
          id: field.id,
          value: sectionValues[field.id]
        };
      })
    : [];

export const getServiceObjectsValuesForSubmit = (
  section: CustomDataSection,
  sectionValues: {
    [key: string]: any;
  }
): ServiceObjectFieldValue[] => {
  return Object.keys(sectionValues).map((key) => {
    return {
      serviceObjectId: key,
      fields: sectionValues[key] ? getFieldsValuesForSubmit(section, sectionValues[key]) : []
    };
  });
};

export const getCustomDataForSubmit = (
  sections: CustomDataSection[],
  values: SubmittedCustomData
): CustomDataObject => {
  return {
    version: '1',
    sections: sections.reduce((acc: CustomDataSectionValues[], section) => {
      const sectionValues = values[section.id];

      if (!sectionValues) {
        return [
          ...acc,
          {
            id: section.id,
            data: []
          }
        ];
      }

      if (
        section.id === 'TravelCosts' ||
        section.id === 'WorkTable' ||
        section.id === 'Material' ||
        section.id === 'Signature'
      ) {
        return [
          ...acc,
          {
            id: section.id,
            data: sectionValues as any
          }
        ];
      }

      // Backward compatibility
      if (section.id === 'CheckList') {
        return [
          ...acc,
          {
            id: section.id,
            data: Object.keys(sectionValues).map((optionId) => {
              return {
                id: optionId,
                value: getCheckListItemValue(sectionValues[optionId]?.value),
                description: sectionValues[optionId]?.description
              };
            })
          }
        ];
      }

      if (section.sections) {
        const subSectionsValues = section.sections.map((subSection) => {
          const subSectionValues = sectionValues[subSection.id];

          if (section.perServiceObject) {
            return {
              id: `${section.id}.${subSection.id}`,
              data: subSectionValues
                ? getServiceObjectsValuesForSubmit(subSection, subSectionValues)
                : []
            };
          }

          return {
            id: `${section.id}.${subSection.id}`,
            data: getFieldsValuesForSubmit(subSection, subSectionValues)
          };
        });

        return [...acc, ...subSectionsValues];
      }

      if (section.perServiceObject) {
        return [
          ...acc,
          {
            id: section.id,
            data: sectionValues ? getServiceObjectsValuesForSubmit(section, sectionValues) : []
          }
        ];
      }

      return [
        ...acc,
        {
          id: section.id,
          data: getFieldsValuesForSubmit(section, sectionValues)
        }
      ];
    }, [])
  };
};

// Return fields with empty data so every configured field is present in the initial form values.
// On CustomFormPage it is replaced with getCustomDataDefaultValues
export const getCustomDataEmptyValues = (sections: CustomDataSection[]) => {
  return sections.reduce((acc: { [sectionId: string]: any }, section) => {
    if (section.fields) {
      acc[section.id] = section.fields?.reduce((acc: { [fieldId: string]: any }, field) => {
        acc[field.id] = undefined;
        return acc;
      }, {});
      return acc;
    }

    acc[section.id] = undefined;
    return acc;
  }, {});
};

export const getCustomDataInitialValues = (
  sections: CustomDataSection[],
  data: CustomDataObject
) => {
  return data.sections.reduce((acc: { [sectionId: string]: any }, section) => {
    const templateSection = sections.find((x) => x.id === section.id);

    if (templateSection?.id === 'TotalCost') {
      return acc;
    }

    if (
      templateSection?.id === 'WorkTable' ||
      templateSection?.id === 'TravelCosts' ||
      templateSection?.id === 'Material' ||
      templateSection?.id === 'Signature'
    ) {
      acc[section.id] = section.data;
      return acc;
    }

    // Backward compatibility
    if (templateSection?.id === 'CheckList') {
      acc[section.id] = (section.data as CheckListItemValue[]).reduce(
        (acc, checkListItemValue: CheckListItemValue) => {
          acc[checkListItemValue.id] = {
            description: checkListItemValue.description,
            value: getCheckListItemValueText(checkListItemValue.value)
          };

          return acc;
        },
        {} as any
      );

      return acc;
    }

    // Subsection
    if (section.id.includes('.')) {
      const splittedId = section.id.split('.');

      const toplevelSection = sections.find((x) => x.id === splittedId[0]);
      const subSection = toplevelSection?.sections?.find((x) => x.id === splittedId[1]);

      if (!subSection || !toplevelSection) return acc;

      const subSectionValues = toplevelSection.perServiceObject
        ? getServiceObjectsInitialValues(section.data as ServiceObjectFieldValue[], subSection)
        : getSectionInitialValues(section.data as CustomDataFieldValue[], subSection);

      acc[toplevelSection.id] = {
        ...acc[toplevelSection.id],
        [subSection.id]: subSectionValues
      };

      return acc;
    }

    if (templateSection?.perServiceObject) {
      acc[section.id] = getServiceObjectsInitialValues(
        section.data as ServiceObjectFieldValue[],
        templateSection
      );
      return acc;
    }

    acc[section.id] = getSectionInitialValues(
      section.data as CustomDataFieldValue[],
      templateSection
    );
    return acc;
  }, {});
};

export const transformDefaultValue = (
  defaultValue: DefaultValue,
  linkedData: LinkedDataObject,
  options?: Option[]
) => {
  if (defaultValue.type === 'Date') {
    return getNow(); // TODO: in future we will transform date by value maybe
  }

  if (defaultValue.type === 'Select') {
    return {
      value: defaultValue.value,
      stringValue: options?.find((x) => x.id === defaultValue.value)?.label
    }; // TODO: in future we will handle multichoice
  }

  if (defaultValue.type === 'OrderAddress') {
    return linkedData.order ? oneLineAddress(linkedData.order.address) : '';
  }

  if (defaultValue.type === 'ContactPersonName') {
    return linkedData.order ? getFullName(linkedData.order.contactPerson) : '';
  }

  if (defaultValue.type === 'AssigneeName') {
    const assingee = linkedData.users?.find(
      (x) => x.id === linkedData.intervention?.primaryAssigneeId
    );
    return assingee ? getFullName(assingee) : '';
  }

  return defaultValue.value;
};

export const getLinkValue = (field: CustomDataField, object: any) => {
  const linkValue = lodashGet(object, field.link!);

  if (field.type === 'Select' && !field.isMultiSelect && linkValue) {
    if (linkValue.value === null || linkValue.value === undefined) return undefined;

    return linkValue;
  }

  return linkValue !== null && linkValue !== undefined ? linkValue : undefined;
};

export const getCustomFieldsDefaultValues = (
  fields: CustomDataField[],
  linkedData: LinkedDataObject
) => {
  return fields.reduce((acc: { [fieldId: string]: any }, field) => {
    if (field.link) {
      acc[field.id] = getLinkValue(field, linkedData);
      return acc;
    }

    if (field.defaultValue) {
      acc[field.id] = transformDefaultValue(field.defaultValue, linkedData, field.options);
      return acc;
    }

    if (field.type === 'CheckList' && field.options) {
      const defaultOptions = field.options.reduce(
        (acc: Record<string, CheckListInputValue>, option) => {
          // Prefix the option ID so the select always returns the complete option object.
          const optionId = `${CHECKLIST_OPTION_ID_PREFIX}${option.id}`;

          acc[optionId] = {
            value: '',
            description: ''
          };

          return acc;
        },
        {}
      );

      acc[field.id] = defaultOptions;
      return acc;
    }

    acc[field.id] = undefined;
    return acc;
  }, {});
};

export const getServiceObjectsDefaultValues = (
  serviceObjectsIds: string[],
  section: CustomDataSection,
  linkedData: LinkedDataObject,
  serviceObjectCategories: ServiceObjectCategory[]
) => {
  return serviceObjectsIds.reduce((acc: { [serviceObjectId: string]: any }, serviceObjectId) => {
    const so = linkedData.serviceObjects?.find((x) => x.id === serviceObjectId);

    // Check if serviceObject is filtered from this section
    if (
      so?.categorySlug &&
      section.serviceObjectCategories &&
      !section.serviceObjectCategories?.includes(so?.categorySlug)
    ) {
      return acc;
    }

    const serviceObjectTemplate =
      so && serviceObjectCategories.find((x) => x.id === so.categoryId)?.customDataTemplate;

    const transformedSo: LinkedDataServiceObjectInCustomer | undefined = so && {
      ...so,
      revisionCategories: so.revisionCategories.reduce(
        (acc: Record<string, ServiceObjectsServiceObjectRevisionCategory>, category) => {
          if (category.slug) {
            acc[category.slug] = category;
          }

          return acc;
        },
        {}
      ),
      customData:
        serviceObjectTemplate &&
        so?.customData &&
        (getCustomDataInitialValues(serviceObjectTemplate.sections, so?.customData) as any)
    };

    acc[serviceObjectId] = section.fields?.reduce((acc: any, field) => {
      if (field.link) {
        acc[field.id] = getLinkValue(field, { ...linkedData, serviceObject: transformedSo });
        return acc;
      }

      if (field.defaultValue) {
        acc[field.id] = transformDefaultValue(field.defaultValue, linkedData, field.options);
        return acc;
      }

      if (field.type === 'CheckList' && field.options) {
        const defaultOptions = field.options.reduce(
          (acc: Record<string, CheckListInputValue>, option) => {
            // Prefix the option ID so the select always returns the complete option object.
            const optionId = `${CHECKLIST_OPTION_ID_PREFIX}${option.id}`;

            acc[optionId] = {
              value: '',
              description: ''
            };

            return acc;
          },
          {}
        );

        acc[field.id] = defaultOptions;
        return acc;
      }

      acc[field.id] = undefined;
      return acc;
    }, {});

    return acc;
  }, {});
};

export const getCustomDataDefaultValues = (
  sections: CustomDataSection[],
  serviceObjectCategories: ServiceObjectCategory[],
  linkedData: LinkedDataObject,
  serviceObjectIds?: string[]
) => {
  return sections.reduce((acc: { [sectionId: string]: any }, section) => {
    if (section.id === 'TravelCosts' || section.id === 'Material') {
      acc[section.id] = [];
      return acc;
    }

    if (section.id === 'TotalCost' || section.id === 'Signature') {
      return acc;
    }

    if (section.id === 'WorkTable') {
      if (
        section.assignee !== 'none' &&
        linkedData.intervention &&
        linkedData.intervention.assignations.length > 1
      ) {
        acc[section.id] = linkedData.intervention?.assignations.map((assignation) => ({
          id: guidGenerator(),
          assigneeId: assignation.userId,
          amount: 1
        }));
      } else {
        acc[section.id] = [];
      }

      return acc;
    }

    if (section.sections) {
      if (section.perServiceObject) {
        acc[section.id] = section.sections.reduce(
          (acc: { [sectionId: string]: any }, subSection) => {
            acc[subSection.id] = getServiceObjectsDefaultValues(
              serviceObjectIds || [],
              subSection,
              linkedData,
              serviceObjectCategories
            );

            return acc;
          },
          {}
        );

        return acc;
      }

      acc[section.id] = section.sections.reduce(
        (acc: { [subsectionId: string]: any }, subSection) => {
          acc[subSection.id] = getCustomFieldsDefaultValues(subSection.fields || [], linkedData);
          return acc;
        },
        {}
      );

      return acc;
    }

    if (section.perServiceObject && serviceObjectIds) {
      acc[section.id] = getServiceObjectsDefaultValues(
        serviceObjectIds,
        section,
        linkedData,
        serviceObjectCategories
      );

      return acc;
    }

    acc[section.id] = {
      ...getCustomFieldsDefaultValues(section.fields || [], linkedData)
    };

    return acc;
  }, {});
};

// TODO: handle other fixedData fields

export const handleTransformRelatedFieldValue = (
  path: string,
  type: RelatedFieldTransformType,
  object: RelatedFieldObject,
  formatter?: string
) => {
  const i18n = getI18n();

  switch (type) {
    case 'time':
      return lodashGet({ ...object }, path)
        ? dayjs.utc(lodashGet({ ...object }, path)).format(formatter || 'LT')
        : '';
    case 'date':
      return lodashGet({ ...object }, path)
        ? dayjs.utc(lodashGet({ ...object }, path)).format(formatter || 'LL')
        : '';
    case 'datetime':
      return lodashGet({ ...object }, path)
        ? dayjs.utc(lodashGet({ ...object }, path)).format(formatter || 'LLL')
        : '';
    case 'boolean':
      return lodashGet({ ...object }, path) ? i18n.t('misc.okText') : i18n.t('misc.noText');
    case 'currency': {
      return valueCurrencyShortcut(
        lodashGet({ ...object }, path) as number,
        object.company!.currencyId,
        i18n.t
      );
    }
    case 'orderCode':
      return object.order!.code;
    case 'oneLineAddress':
      return oneLineAddress(lodashGet({ ...object }, path));
    case 'assignations': {
      const assignations = lodashGet({ ...object }, path) as RelatedDataInterventionAssignation[];
      return assignations ? assignations.map((x) => x.fullName).join(', ') : '';
    }

    default:
      return lodashGet({ ...object }, path);
  }
};

export const interpolateRelatedFieldTemplate = (
  template: string,
  object: RelatedFieldObject,
  serviceObject?: ServiceObjectInRelatedData
) => {
  const objectMergedWithServiceObject = { ...object, serviceObject };

  return template.replace(/\{\{([^}]+)\}\}/g, (match, group: string) => {
    const groupByFormatter = group.split('|');
    const groupTransform = groupByFormatter[0].split(':');
    if (groupTransform[1]) {
      return handleTransformRelatedFieldValue(
        groupTransform[0],
        groupTransform[1] as RelatedFieldTransformType,
        objectMergedWithServiceObject,
        groupByFormatter[1]
      );
    }

    return lodashGet({ ...objectMergedWithServiceObject }, group) || '';
  });
};

const mappedSectionErrors = (errors: { [key: string]: any }, section: CustomDataSection) => {
  const i18n = getI18n();

  return Object.keys(errors).reduce((acc: MappedFieldError[], fieldId: string) => {
    const field = section.fields?.find((x) => x.id === fieldId);

    if (field?.type === 'GeoCoordinate') {
      const locationErrors = errors[fieldId];

      const locationMappedErrors = locationErrors
        ? Object.keys(locationErrors).map((field) => ({
            label: i18n.t(`customData.forms.${field}`),
            error: locationErrors[field]
          }))
        : [];

      acc.push({ label: field.label, errors: locationMappedErrors, multipleErrors: true });
    } else if (field?.type === 'CheckList') {
      const fieldErrors = errors[fieldId];

      const checkListErrors = Object.keys(fieldErrors).reduce(
        (acc: MappedFieldError[], optionId) => {
          // Prefix the option ID so the select always returns the complete option object.
          // So we must split the key to get real option id
          const splittedOptionId = optionId.split(CHECKLIST_OPTION_ID_PREFIX);
          const option = field.options?.find((x) => x.id.toString() === splittedOptionId[1]);

          const optionErrors = option && fieldErrors[optionId];

          const optionMappedErrors = optionErrors
            ? Object.keys(optionErrors).map((field) => {
                const fieldError: FieldError = optionErrors[field];

                return {
                  label: option.label,
                  error: `${i18n.t(`customData.${field}`)} - ${fieldError.message}`
                };
              })
            : [];

          return [...acc, ...optionMappedErrors];
        },
        []
      );

      acc.push({
        label: field.label,
        multipleErrors: true,
        errors: checkListErrors
      });
    } else {
      const fieldError: FieldError = lodashGet(errors, `${fieldId}`);

      acc.push({
        label: field?.label,
        error: fieldError.message
      });
    }
    return acc;
  }, []);
};

const getMappedServiceObjectsErrors = (
  errors: { [key: string]: any },
  section: CustomDataSection,
  serviceObjects?: ServiceObjectInCustomer[]
) => {
  return Object.keys(errors).reduce((acc: MappedErrorsSection[], serviceObjectId) => {
    const serviceObjectName = serviceObjects?.find((x) => x.id === serviceObjectId)?.name;

    acc.push({
      label: `${serviceObjectName} - ${section.title}`,
      errors: mappedSectionErrors(errors[serviceObjectId], section)
    });
    return acc;
  }, []);
};

export const getMappedFieldErrors = (
  sections: CustomDataSection[],
  serviceObjects?: ServiceObjectInCustomer[],
  errors?: { [key: string]: any }
): MappedErrorsSection[] => {
  if (!errors) {
    return [];
  }

  return Object.keys(errors).reduce((acc: MappedErrorsSection[], sectionId) => {
    const templateSection = sections.find((x) => x.id === sectionId);

    const i18n = getI18n();

    if (
      templateSection &&
      (templateSection.id === 'WorkTable' ||
        templateSection.id === 'TravelCosts' ||
        templateSection.id === 'Material')
    ) {
      const sectionErrors = lodashGet(errors, sectionId);

      let mappedSectionErrors: MappedFieldError[] = [];

      if (Array.isArray(sectionErrors)) {
        mappedSectionErrors = sectionErrors.reduce((acc: MappedFieldError[], itemError: any) => {
          const itemMappedError = itemError
            ? Object.keys(itemError).map((key) => ({
                error: `${i18n.t(`customData.${key}`)} - ${itemError[key].message}`
              }))
            : [];

          return [...acc, ...itemMappedError];
        }, []);
      } else {
        mappedSectionErrors = [
          {
            error: sectionErrors
          }
        ];
      }

      acc.push({
        label: templateSection.title,
        errors: mappedSectionErrors
      });
      return acc;
    }

    // Backward compatibility
    if (templateSection && templateSection.id === 'CheckList') {
      const sectionErrors = errors[templateSection.id];

      const checkListErrors = Object.keys(sectionErrors).reduce(
        (acc: MappedFieldError[], optionId) => {
          const option = templateSection.options?.find((x) => x.id.toString() === optionId);

          const optionErrors = option && sectionErrors[option.id];

          const optionMappedErrors = optionErrors
            ? Object.keys(optionErrors).map((field) => {
                return {
                  label: option.label,
                  error: `${i18n.t(`customData.${field}`)} - ${optionErrors[field] as string}`
                };
              })
            : [];

          return [...acc, ...optionMappedErrors];
        },
        []
      );

      acc.push({
        label: templateSection.title,
        errors: checkListErrors
      });

      return acc;
    }

    if (templateSection?.sections) {
      const sectionErrors = errors[sectionId];

      if (sectionErrors) {
        const topLevelSectionErrors = templateSection.sections.reduce(
          (acc: MappedErrorsSection[], subSection) => {
            const subSectionErrors = sectionErrors[subSection.id];

            if (subSectionErrors) {
              const mappedSubSectionErrors = templateSection.perServiceObject
                ? getMappedServiceObjectsErrors(subSectionErrors, subSection, serviceObjects)
                : [
                    {
                      label: templateSection.title,
                      errors: mappedSectionErrors(subSectionErrors, subSection)
                    }
                  ];

              return [...acc, ...mappedSubSectionErrors];
            }

            return acc;
          },
          []
        );

        return [...acc, ...topLevelSectionErrors];
      }

      return acc;
    }

    if (templateSection?.perServiceObject) {
      const serviceObjectsErrors = getMappedServiceObjectsErrors(
        errors[sectionId],
        templateSection,
        serviceObjects
      );

      acc.push(...serviceObjectsErrors);
      return acc;
    }

    if (templateSection) {
      acc.push({
        label: templateSection.title,
        errors: mappedSectionErrors(errors[sectionId], templateSection)
      });
      return acc;
    }

    return acc;
  }, []);
};

export const getTotalCostData = (data: CustomDataObject): TotalCostData => {
  const totalCostData: TotalCostData = {
    Material: data.sections.find((x) => x.id === 'Material')?.data as OrderMaterial[],
    WorkTable: data.sections.find((x) => x.id === 'WorkTable')?.data as Work[],
    TravelCosts: data.sections.find((x) => x.id === 'TravelCosts')?.data as TravelCost[]
  };

  return totalCostData;
};

export const getCustomFormHidePriceParams = (
  sections: CustomDataSection[]
): TotalCostHidePriceParams => {
  let params: TotalCostHidePriceParams = {
    material: false,
    travelCosts: false,
    workTable: false
  };

  if (sections.find((x) => x.id === 'Material')?.hidePrice) {
    params.material = true;
  }

  if (sections.find((x) => x.id === 'WorkTable')?.hidePrice) {
    params.workTable = true;
  }

  if (sections.find((x) => x.id === 'TravelCosts')?.hidePrice) {
    params.travelCosts = true;
  }

  return params;
};

export const calculateTotalTravelCost = (
  travelCosts: TravelCost[],
  hasFlatRateTravelFee?: boolean,
  travelFee?: number | null,
  pricePerKm?: number
) => {
  return hasFlatRateTravelFee
    ? travelFee || 0
    : travelCosts.reduce((acc, cur) => {
        return acc + (cur.distance || 0) * (pricePerKm || 0);
      }, 0);
};

export const getTotalCost = (
  values: any,
  hidePriceParams: TotalCostHidePriceParams,
  hasFlatRateTravelFee?: boolean,
  travelFee?: number | null,
  pricePerKm?: number
) => {
  let totalCost = 0;

  // Material
  totalCost +=
    values.Material && !hidePriceParams.material
      ? values.Material.reduce((acc: number, cur: OrderMaterial) => {
          return acc + (cur.amount || 0) * (cur.price || 0);
        }, 0)
      : 0;

  // Travel
  totalCost +=
    values.TravelCosts && !hidePriceParams.travelCosts
      ? calculateTotalTravelCost(values.TravelCosts, hasFlatRateTravelFee, travelFee, pricePerKm)
      : 0;

  // Work
  totalCost +=
    values.WorkTable && !hidePriceParams.workTable
      ? values.WorkTable.reduce((acc: number, cur: Work) => {
          return acc + (cur.price || 0) * (cur.amount || 0);
        }, 0)
      : 0;

  return totalCost;
};

export const compareDataWithTemplate = (
  values: SubmittedCustomData,
  sections?: CustomDataSection[]
): CompareDataWithTemplateResult => {
  const filledValues =
    values &&
    Object.keys(values).every((sectionId) => {
      const valuesSection = values[sectionId];
      return Object.keys(valuesSection).every((fieldId) => valuesSection[fieldId]);
    });

  if (!sections || !values || !filledValues) {
    return {
      isDifference: false,
      data: values
    };
  }

  let isDifference = false;

  Object.keys(values).forEach((sectionId) => {
    const templateSection = sections.find((x) => x.id === sectionId);

    if (!templateSection) {
      isDifference = true;
    } else {
      Object.keys(values[sectionId]).forEach((fieldId) => {
        const templateField = templateSection.fields?.find((x) => x.id === fieldId);

        if (!templateField) {
          isDifference = true;
        }
      });
    }
  });

  if (!isDifference) {
    return {
      isDifference,
      data: values
    };
  }

  const mappedDataToNewTemplate = sections.reduce((acc: Record<string, any>, section) => {
    const dataSection = values[section.id];

    if (dataSection) {
      const newTemplateSecitonData =
        section.fields?.reduce((acc: Record<string, any>, field) => {
          const dataField = dataSection[field.id];

          if (dataField) {
            acc[field.id] = dataField;
            return acc;
          }

          return acc;
        }, {}) || {};

      acc[section.id] = newTemplateSecitonData;
      return acc;
    }

    return acc;
  }, {});

  return {
    isDifference,
    data: mappedDataToNewTemplate
  };
};
